import React, { useState } from "react";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useProductSearch } from "@/hooks/useProductSearch";

// UC-163 Work Item 4: Support unlinked identifiers (null master_product_id)
export interface SelectedProduct {
  master_product_id: string | null;
  display_label: string;
  identifier_type?: string;
  identifier_value?: string;
}

interface Props {
  organisationId: string;
  value: SelectedProduct | null;
  onSelect: (next: SelectedProduct | null) => void;
  disabled?: boolean;
}

// UC-163 Work Item 7: Accessibility improvements (advisory)
// Covers AC-UC163 Accessibility requirements and A11Y-163-001/002/003
const ProductIdentifierCombobox: React.FC<Props> = ({ organisationId, value, onSelect, disabled }) => {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  const { data: results = [], isFetching } = useProductSearch(organisationId, term);

  const comboboxId = React.useId();
  const listboxId = `${comboboxId}-listbox`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-label="Search and select product by identifier"
          disabled={disabled}
          className="w-full justify-between group"
        >
          {value ? (
            <span className="truncate">{value.display_label}</span>
          ) : (
            <span className="text-muted-foreground group-hover:text-accent-foreground">Search product by SKU / UPC / ASIN…</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false} id={listboxId}>
          <CommandInput
            placeholder="Type to search…"
            value={term}
            onValueChange={setTerm}
            aria-label="Product identifier search input"
          />
          <CommandList role="listbox" aria-live="polite" aria-busy={isFetching}>
            {isFetching && (
              <div role="status" className="flex items-center justify-center py-4 text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> 
                <span>Searching…</span>
              </div>
            )}
            {!isFetching && term.length === 0 && (
              <div role="status" className="py-4 text-center text-sm text-muted-foreground">
                Start typing an identifier to find a product.
              </div>
            )}
            {!isFetching && term.length > 0 && results.length === 0 && (
              <CommandEmpty role="status">Map this identifier to a Master product before adjusting stock.</CommandEmpty>
            )}
            {results.length > 0 && (
              <CommandGroup>
                {results.map((r) => {
                  const primary = r.identifiers.find(
                    (identifier) =>
                      identifier.identifier_value.toLowerCase().trim() === r.preferred_identifier_value?.toLowerCase().trim() &&
                      identifier.identifier_type.toLowerCase().trim() === r.preferred_identifier_type?.toLowerCase().trim(),
                  ) ?? r.identifiers[0];
                  const label = `${primary?.identifier_type.toUpperCase()} ${primary?.identifier_value}`;
                  const isSelected = value?.master_product_id === r.master_product_id;
                  // UC-163 Work Item 4: Handle null master_product_id for unlinked identifiers
                  const itemKey = r.master_product_id ?? `unlinked-${r.preferred_identifier_type}-${r.preferred_identifier_value}`;
                  const itemValue = r.master_product_id ?? itemKey;
                  return (
                    <CommandItem
                      key={itemKey}
                      value={itemValue}
                      onSelect={() => {
                        onSelect({
                          master_product_id: r.master_product_id,
                          display_label: label,
                          identifier_type: primary?.identifier_type,
                          identifier_value: primary?.identifier_value,
                        });
                        setOpen(false);
                      }}
                      className="data-[selected=true]:text-accent-foreground [&[data-selected=true]_span]:text-accent-foreground"
                    >
                      <Check className={cn("mr-2 h-4 w-4", isSelected ? "opacity-100" : "opacity-0")} />
                      <div className="flex flex-col">
                        <span className="text-sm text-foreground">
                          {r.identifiers
                            .slice(0, 3)
                            .map((i) => `${i.identifier_type.toUpperCase()} ${i.identifier_value}`)
                            .join(" · ")}
                          {r.identifiers.length > 3 ? ` · +${r.identifiers.length - 3} more` : ""}
                        </span>
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default ProductIdentifierCombobox;
